import { z } from "zod";
/**
 * Claim + Evidence + Stance epistemology (ADR-0117).
 *
 * A CLAIM is a proposition. EVIDENCE is a separately identifiable record that
 * supports, contradicts or merely reports a claim and keeps its own
 * provenance. A STANCE is one actor's relationship to a claim. The three stay
 * separate, and the controlled dimensions below stay orthogonal: canonical
 * storage of a record never implies verification, adoption, counterparty
 * assertion, agreement or current organisational position.
 *
 * This module is dependency-free apart from zod so the portal SPA can import
 * the published projection without pulling staff-side code.
 */
export const CLAIM_TYPES = [
    "observation",
    "statement",
    "position",
    "decision",
    "assumption",
    "proposal",
    "scenario",
    "question",
    "derived",
];
export const ClaimTypeSchema = z.enum(CLAIM_TYPES);
export const EVIDENCE_RELATIONS = ["supports", "contradicts", "reports"];
export const EvidenceRelationSchema = z.enum(EVIDENCE_RELATIONS);
export const EVIDENCE_AUTHORITIES = [
    "primary",
    "controlled-current",
    "draft",
    "secondary",
    "conversational",
    "derived",
];
export const EvidenceAuthoritySchema = z.enum(EVIDENCE_AUTHORITIES);
/** `unknown` is an explicit "not recorded"; absence of a stance means the same. */
export const STANCES = [
    "adopted",
    "rejected",
    "proposed",
    "agreed",
    "disputed",
    "unknown",
];
export const StanceSchema = z.enum(STANCES);
export const VERIFICATIONS = [
    "verified",
    "corroborated",
    "source-only",
    "unverified",
    "contradicted",
];
export const VerificationSchema = z.enum(VERIFICATIONS);
/**
 * `self` is the tenant organisation itself (whoever operates this Insights
 * tenant). `external` is anyone else — a counterparty, regulator, supplier or
 * person. No organisation names are built into the model.
 */
export const ACTOR_KINDS = ["self", "external"];
export const ActorKindSchema = z.enum(ACTOR_KINDS);
/** The three legacy-compatibility states (ADR-0117 §Migration). */
export const CLASSIFICATION_STATES = [
    "explicit",
    "machine-classified",
    "unclassified-legacy",
];
export const ClassificationStateSchema = z.enum(CLASSIFICATION_STATES);
/**
 * Attribution is computed, never stored as a single field:
 * - `none` — the claim is not attributed to anyone ("X is true" form).
 * - `reported` — a source says that the actor said X; the actor has not been
 *   shown to say it themselves.
 * - `direct` — the actor's own record or own stance establishes it.
 */
export const ATTRIBUTION_STATUSES = ["none", "reported", "direct"];
export const AttributionStatusSchema = z.enum(ATTRIBUTION_STATUSES);
/**
 * Who authored the PROPOSITION (AI Inference Boundary). `model` claims are
 * always `derived`, always stored `unverified`, never attributed and never
 * bound to a fact — enforced by DB CHECK as well as here.
 */
export const CLAIM_ORIGINS = ["human", "model"];
export const ClaimOriginSchema = z.enum(CLAIM_ORIGINS);
/**
 * Reasoning mode of a derived claim. Retrieval is not a claim (it is the
 * evidence-backed classes); organisational knowledge is a stance; a gap (the
 * records are silent) is transient reasoning, never a claim. These four are
 * the inference operations that may create a claim:
 * - `synthesis` — combines several records into a pattern ("taken together");
 * - `hypothesis` — a possibility worth investigating; never promoted by
 *   repetition;
 * - `scenario` — a consequence that holds only under a stated condition;
 * - `implication` — what would follow from the named proposition(s) if they
 *   hold; its lineage must name at least one claim or fact it follows from.
 */
export const INFERENCE_MODES = ["synthesis", "hypothesis", "scenario", "implication"];
export const InferenceModeSchema = z.enum(INFERENCE_MODES);
/**
 * Who may record EVIDENCE or a STANCE. There is deliberately no `model`
 * value: inference may create a claim, never evidence or stance.
 */
export const AUTHORITY_RECORDER_KINDS = ["human", "integration"];
export const AuthorityRecorderKindSchema = z.enum(AUTHORITY_RECORDER_KINDS);
const ASOF_RE = /^\d{4}-\d{2}-\d{2}(T[\d:.]+(Z|[+-]\d{2}:\d{2}))?$/;
const AsOfSchema = z.string().regex(ASOF_RE, "asOf must be YYYY-MM-DD or an ISO-8601 timestamp");
export const EpistemicActorSchema = z.object({
    kind: ActorKindSchema,
    label: z.string().trim().min(1).max(200),
    /** Optional graph node for the actor (an organisation Subject). */
    nodeId: z.string().uuid().nullable().default(null),
});
export const EPISTEMIC_SOURCE_REF_KINDS = [
    "document",
    "transcript",
    "chat",
    "note",
    "manual",
];
export const EpistemicEvidenceCreateSchema = z.object({
    relation: EvidenceRelationSchema,
    authority: EvidenceAuthoritySchema,
    /** Mandatory provenance: document name, brief section, person, ticket. */
    source: z.string().trim().min(1).max(400),
    /** Mandatory provenance: when the evidence said what it says. */
    asOf: AsOfSchema,
    /**
     * Who authored the evidence itself — distinct from the claim's
     * `attributedTo`. A self-authored draft that says "MoIP wishes…" has
     * sourceActor=self, while the claim is attributed to MoIP.
     */
    sourceActor: EpistemicActorSchema.nullable().default(null),
    sourceRef: z
        .object({
        kind: z.enum(EPISTEMIC_SOURCE_REF_KINDS),
        ref: z.string().min(1).max(400),
    })
        .nullable()
        .default(null),
    excerpt: z.string().max(2_000).nullable().default(null),
    /** Explanatory only. Never read by selection, eligibility or guards. */
    note: z.string().max(1_000).nullable().default(null),
});
export const EpistemicStanceCreateSchema = z.object({
    actor: EpistemicActorSchema,
    stance: StanceSchema,
    source: z.string().trim().min(1).max(400),
    asOf: AsOfSchema,
    note: z.string().max(1_000).nullable().default(null),
});
export const LINEAGE_KINDS = ["claim", "evidence", "fact"];
export const EpistemicLineageRefSchema = z.object({
    kind: z.enum(LINEAGE_KINDS),
    /** Claim id, evidence id (uuid) or a `fact:` citation id on the same Subject. */
    id: z.string().min(1).max(200),
});
function refineDerivedShape(value, ctx) {
    const derived = value.claimType === "derived";
    if (derived && value.derivedFrom.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["derivedFrom"],
            message: "A derived claim must record the claims, evidence or facts it was derived from.",
        });
    }
    if (derived && !value.inferenceMode) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["inferenceMode"],
            message: "A derived claim must say whether it is a synthesis, hypothesis, scenario or implication.",
        });
    }
    if (!derived && value.inferenceMode) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["inferenceMode"],
            message: "inferenceMode applies to derived claims only.",
        });
    }
    if ((value.inferenceMode === "scenario" || value.claimType === "scenario") && !value.condition) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["condition"],
            message: "A scenario must state the condition its consequence depends on.",
        });
    }
    if (value.inferenceMode === "implication" && !namesProposition(value.derivedFrom)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["derivedFrom"],
            message: "An implication must name the proposition(s) it follows from: at least one claim or fact in derivedFrom.",
        });
    }
}
/** True when lineage names at least one proposition (a claim or a fact), not only evidence rows. */
export function namesProposition(derivedFrom) {
    return derivedFrom.some((ref) => {
        const kind = ref?.kind;
        return kind === "claim" || kind === "fact";
    });
}
/**
 * The ONLY shape a model (Insights AI, MCP, any synthesis flow) may use to
 * record knowledge. It can express a proposition, its reasoning mode and its
 * lineage — and nothing that confers authority: no evidence, no stance, no
 * verification, no attribution, no fact binding. `.strict()` rejects any
 * attempt to smuggle those fields in.
 */
export const ModelDerivedClaimCreateSchema = z
    .object({
    proposition: z.string().trim().min(1).max(2_000),
    inferenceMode: InferenceModeSchema,
    condition: z.string().trim().min(1).max(1_000).nullable().default(null),
    derivedFrom: z.array(EpistemicLineageRefSchema).min(1).max(50),
    derivationNote: z.string().max(2_000).nullable().default(null),
})
    .strict()
    .superRefine((value, ctx) => refineDerivedShape({ ...value, claimType: "derived" }, ctx));
export const EpistemicClaimCreateSchema = z
    .object({
    proposition: z.string().trim().min(1).max(2_000),
    claimType: ClaimTypeSchema,
    /** The actor to whom the proposition is attributed, if any. */
    attributedTo: EpistemicActorSchema.nullable().default(null),
    /**
     * Anchor this claim to the current structured fact with this key on the
     * Subject. The binding is to the exact fact tuple (citation id), so a
     * later rewrite of the fact does not inherit this claim's stances.
     */
    factKey: z
        .string()
        .regex(/^[a-z0-9][a-z0-9_]*$/)
        .max(80)
        .nullable()
        .default(null),
    /** Explicit fact citation when the key has competing current values. */
    factCitationId: z.string().min(1).max(200).nullable().default(null),
    verification: VerificationSchema.default("unverified"),
    /** Required for claimType=derived. */
    inferenceMode: InferenceModeSchema.nullable().default(null),
    /** The condition a scenario's consequence depends on ("If X occurs"). */
    condition: z.string().trim().min(1).max(1_000).nullable().default(null),
    /**
     * Lineage. Required for `derived`; also allowed on other types so a
     * hypothesis converted into an open question keeps where it came from.
     */
    derivedFrom: z.array(EpistemicLineageRefSchema).max(50).default([]),
    derivationNote: z.string().max(2_000).nullable().default(null),
    note: z.string().max(1_000).nullable().default(null),
    evidence: z.array(EpistemicEvidenceCreateSchema).max(20).default([]),
    stances: z.array(EpistemicStanceCreateSchema).max(20).default([]),
})
    .superRefine((value, ctx) => refineDerivedShape(value, ctx));
export const EpistemicEvidenceRecordSchema = EpistemicEvidenceCreateSchema.extend({
    id: z.string().uuid(),
    claimId: z.string().uuid(),
    recordedByKind: AuthorityRecorderKindSchema,
    recordedBy: z.string().max(320),
    recordedAt: z.string(),
});
export const EpistemicStanceRecordSchema = EpistemicStanceCreateSchema.extend({
    id: z.string().uuid(),
    claimId: z.string().uuid(),
    recordedByKind: AuthorityRecorderKindSchema,
    recordedBy: z.string().max(320),
    recordedAt: z.string(),
});
export const EpistemicClaimRecordSchema = z.object({
    id: z.string().uuid(),
    subjectId: z.string().uuid(),
    proposition: z.string(),
    claimType: ClaimTypeSchema,
    origin: ClaimOriginSchema,
    inferenceMode: InferenceModeSchema.nullable(),
    condition: z.string().nullable(),
    attributedTo: EpistemicActorSchema.nullable(),
    factKey: z.string().nullable(),
    factCitationId: z.string().nullable(),
    /** As recorded. Read `assessClaim().verification` for the effective value. */
    verification: VerificationSchema,
    derivedFrom: z.array(EpistemicLineageRefSchema),
    derivationNote: z.string().nullable(),
    /** e.g. `staff`, `insights-ai`, `mcp` — the flow that produced the claim. */
    derivationMechanism: z.string().nullable(),
    /** Model id for model-origin claims (null for human reasoning). */
    derivationModel: z.string().nullable(),
    /** When the inference was generated (model-origin claims). */
    generatedAt: z.string().nullable(),
    /**
     * Reasoning provenance: the Insights AI conversation (and turn) the
     * inference was proposed in. Structured, never parsed from the note.
     */
    derivationConversationId: z.string().uuid().nullable().default(null),
    derivationMessageId: z.string().uuid().nullable().default(null),
    note: z.string().nullable(),
    supersededByClaimId: z.string().uuid().nullable(),
    supersededAt: z.string().nullable(),
    /** Why the claim was superseded, and who recorded it (a person). */
    supersededReason: z.string().nullable().default(null),
    supersededBy: z.string().nullable().default(null),
    createdBy: z.string(),
    createdAt: z.string(),
    evidence: z.array(EpistemicEvidenceRecordSchema),
    stances: z.array(EpistemicStanceRecordSchema),
});
export const EpistemicClaimIdParamSchema = z.object({ claimId: z.string().uuid() });
/**
 * Supersession is a human act (ADR-0117 §P2). The superseded claim is kept —
 * with its lineage, evidence and stances — and simply stops being current.
 * `supersededByClaimId` optionally names the replacement on the same Subject.
 */
export const EpistemicClaimSupersedeSchema = z
    .object({
    reason: z.string().trim().min(1).max(1_000),
    supersededByClaimId: z.string().uuid().nullable().default(null),
})
    .strict();
const VERIFICATION_RANK = {
    unverified: 0,
    "source-only": 1,
    corroborated: 2,
    verified: 3,
};
const AUTHORITY_RANK = {
    derived: 0,
    conversational: 1,
    draft: 2,
    secondary: 3,
    "controlled-current": 4,
    primary: 5,
};
const STRONG_AUTHORITIES = new Set([
    "primary",
    "controlled-current",
]);
/** Evidence the named actor authored that counts as the actor's own word. */
const DIRECT_ATTRIBUTION_AUTHORITIES = new Set([
    "primary",
    "controlled-current",
    "secondary",
    "conversational",
]);
export function actorKey(actor) {
    if (actor.nodeId)
        return `node:${actor.nodeId}`;
    return `${actor.kind}:${actor.label.trim().toLowerCase()}`;
}
export function sameActor(a, b) {
    if (a.kind !== b.kind)
        return false;
    if (a.nodeId && b.nodeId)
        return a.nodeId === b.nodeId;
    return a.label.trim().toLowerCase() === b.label.trim().toLowerCase();
}
function stanceOrder(a, b) {
    const byAsOf = a.asOf.localeCompare(b.asOf);
    if (byAsOf !== 0)
        return byAsOf;
    return (a.recordedAt ?? "").localeCompare(b.recordedAt ?? "");
}
export function latestStancesByActor(stances) {
    const latest = new Map();
    for (const row of [...stances].sort(stanceOrder)) {
        latest.set(actorKey(row.actor), row);
    }
    return [...latest.values()].map((row) => ({
        actor: row.actor,
        stance: row.stance,
        asOf: row.asOf,
        source: row.source,
    }));
}
/**
 * What the evidence alone can establish. Contradiction by strong evidence is
 * decisive; `verified` needs strong supporting evidence; `corroborated` needs
 * two independent (distinct-source, non-derived, non-conversational) supports.
 */
export function evidenceVerificationCeiling(evidence) {
    if (evidence.some((row) => row.relation === "contradicts" && STRONG_AUTHORITIES.has(row.authority))) {
        return "contradicted";
    }
    const supports = evidence.filter((row) => row.relation === "supports");
    if (supports.some((row) => STRONG_AUTHORITIES.has(row.authority)))
        return "verified";
    const independent = new Set(supports
        .filter((row) => row.authority !== "derived" && row.authority !== "conversational")
        .map((row) => row.source.trim().toLowerCase()));
    if (independent.size >= 2)
        return "corroborated";
    if (evidence.length > 0)
        return "source-only";
    return "unverified";
}
export function clampVerification(stored, ceiling) {
    if (stored === "contradicted" || ceiling === "contradicted")
        return "contradicted";
    return VERIFICATION_RANK[stored] <= VERIFICATION_RANK[ceiling] ? stored : ceiling;
}
function weakestVerification(values) {
    if (values.length === 0)
        return "unverified";
    if (values.includes("contradicted"))
        return "unverified";
    return values.reduce((low, next) => VERIFICATION_RANK[next] <
        VERIFICATION_RANK[low]
        ? next
        : low);
}
export function assessClaim(claim, resolveLineage = () => null, depth = 0) {
    const derived = claim.claimType === "derived";
    const origin = claim.origin ?? "human";
    let conditional = claim.claimType === "scenario" || claim.inferenceMode === "scenario";
    let verification = clampVerification(claim.verification, evidenceVerificationCeiling(claim.evidence));
    const latest = latestStancesByActor(claim.stances);
    const selfStance = latest.find((row) => row.actor.kind === "self") ?? null;
    // Actor authority: today every stance row is recorded by the tenant's own
    // staff. An EXTERNAL actor's stance is therefore only authoritative when the
    // claim carries that actor's own evidence (a letter, signed minute, email
    // from them) — otherwise it is the organisation's report of their stance and
    // can neither make an attribution direct nor create agreement.
    const actorEvidenced = (actor) => claim.evidence.some((row) => row.sourceActor !== null &&
        sameActor(row.sourceActor, actor) &&
        row.relation !== "contradicts" &&
        DIRECT_ATTRIBUTION_AUTHORITIES.has(row.authority));
    const externalStances = latest
        .filter((row) => row.actor.kind === "external")
        .map((row) => ({ ...row, evidenced: actorEvidenced(row.actor) }));
    let attribution = { actor: null, status: "none" };
    const attributed = claim.attributedTo;
    if (attributed) {
        const ownSelfStance = attributed.kind === "self" &&
            latest.some((row) => sameActor(row.actor, attributed) && row.stance !== "unknown");
        attribution = {
            actor: attributed,
            status: actorEvidenced(attributed) || ownSelfStance ? "direct" : "reported",
        };
    }
    // Agreement is a RELATIONSHIP between actors, derived here — never one
    // actor's stance. It needs the organisation (agreed/adopted) AND at least one
    // evidenced external `agreed`, and fails while any external party's latest
    // stance is `rejected` or `disputed`. A draft, a proposal, an unevidenced
    // report of their view, or silence is never agreement.
    const agreedWith = externalStances
        .filter((row) => row.stance === "agreed" && row.evidenced)
        .map((row) => row.actor);
    const dissentingActors = externalStances
        .filter((row) => row.stance === "rejected" || row.stance === "disputed")
        .map((row) => row.actor);
    let agreed = (selfStance?.stance === "agreed" || selfStance?.stance === "adopted") &&
        agreedWith.length > 0 &&
        dissentingActors.length === 0;
    if (claim.derivedFrom.length > 0) {
        // Lineage is a ceiling for ANY claim that carries it: a derived claim
        // never exceeds its weakest input (ADR-0117 §Derived / epistemic ceiling).
        const inputs = claim.derivedFrom.map((ref) => {
            // A raw fact or evidence row establishes at most what its source says.
            if (ref.kind !== "claim") {
                return { verification: "source-only", input: null };
            }
            const input = depth < 4 ? resolveLineage(ref.id) : null;
            if (!input)
                return { verification: "unverified", input: null };
            return { verification: assessClaim(input, resolveLineage, depth + 1).verification, input };
        });
        verification = clampVerification(verification, weakestVerification(inputs.map((row) => row.verification)));
        const inputAssessments = inputs.map((row) => row.input ? assessClaim(row.input, resolveLineage, depth + 1) : null);
        // Scenario containment: anything reasoned from a scenario stays conditional.
        if (inputAssessments.some((row) => row?.conditional === true))
            conditional = true;
        const allAgreed = inputAssessments.length > 0 && inputAssessments.every((row) => row?.agreed === true);
        // Agreement on a derived proposition must be recorded on the derived claim
        // itself AND already hold for every input.
        agreed = agreed && allAgreed;
        if (attribution.status === "direct" && attributed) {
            const allDirect = inputAssessments.length > 0 &&
                inputAssessments.every((row) => row?.attribution.status === "direct" &&
                    row.attribution.actor !== null &&
                    sameActor(row.attribution.actor, attributed));
            if (!allDirect)
                attribution = { actor: attributed, status: "reported" };
        }
    }
    const bearing = claim.evidence.filter((row) => row.relation !== "contradicts");
    const strongestAuthority = bearing.length === 0
        ? null
        : bearing.reduce((best, row) => AUTHORITY_RANK[row.authority] > AUTHORITY_RANK[best.authority] ? row : best).authority;
    return {
        verification,
        attribution,
        selfStance,
        externalStances,
        agreed,
        agreedWith,
        dissentingActors,
        strongestAuthority,
        derived,
        conditional,
        origin,
        inferenceMode: claim.inferenceMode ?? null,
    };
}
/**
 * Structural rules only. A rule may use the writer identity recorded in
 * `source` when a *system* (not a person) wrote it verbatim; it must never
 * interpret free prose in `value` or `note`. Anything else is
 * `unclassified-legacy` — retrievable, never automatically a position.
 */
export const LEGACY_MACHINE_RULES = [
    {
        // map-overlay-sync.service `safeWritebackKanbanStatus` stamps this exact
        // source when it mirrors CRM Kanban status onto a milestone node.
        id: "crm-kanban-status",
        systemLabel: "CRM Kanban",
        matches: (fact) => fact.source === "crm-kanban" && (fact.key === "status" || fact.key === "status_as_of"),
        claimType: "observation",
        authority: "controlled-current",
    },
];
export function classifyLegacyFact(fact) {
    for (const rule of LEGACY_MACHINE_RULES) {
        if (rule.matches(fact)) {
            return {
                state: "machine-classified",
                claimType: rule.claimType,
                authority: rule.authority,
                rule: rule.id,
                systemLabel: rule.systemLabel,
            };
        }
    }
    return { state: "unclassified-legacy" };
}
export function claimCitationId(claimId) {
    return `claim:${claimId}`;
}
//# sourceMappingURL=epistemic.js.map